import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { formatDate } from '@/lib/utils';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Key, Plus } from 'lucide-react';
import { CreateApiKeyDialog } from '@/components/CreateApiKeyDialog';
import { McpAccess } from '@/components/McpAccess';
import { PageHeader } from '@/components/PageHeader';
import { StatusBadge } from '@/components/StatusBadge';

interface ApiKey {
  id: string;
  name: string;
  keyPrefix: string;
  permissions: string[];
  lastUsedAt?: number;
  last_used_at?: number;
  expiresAt?: number;
  expires_at?: number;
  status: string;
  createdAt?: number;
  created_at?: number;
}

function ApiKeys({ user }: { user?: { role?: string } }) {
  const { t } = useTranslation();
  const [apiKeys, setApiKeys] = useState<ApiKey[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);

  useEffect(() => {
    fetchApiKeys();
  }, []);

  const fetchApiKeys = async () => {
    try {
      const token = localStorage.getItem('token');
      const apiUrl = import.meta.env.VITE_API_URL || '';

      const response = await fetch(`${apiUrl}/api/apiKeys`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        setApiKeys(data.data || []);
      }
    } catch (error) {
      console.error('Failed to fetch API keys:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleRevoke = async (id: string) => {
    try {
      const token = localStorage.getItem('token');
      const apiUrl = import.meta.env.VITE_API_URL || '';

      const response = await fetch(`${apiUrl}/api/apiKeys/${id}`, {
        method: 'DELETE',
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.ok) {
        fetchApiKeys();
      }
    } catch (error) {
      console.error('Failed to revoke API key:', error);
    }
  };

  if (isLoading) {
    return (
      <div className="w-full px-4 py-6 sm:px-6">
        <Card>
          <CardContent className="pt-6">
            <p className="text-center text-muted-foreground">{t('common.loading')}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="w-full space-y-6 px-4 py-6 sm:px-6 lg:px-8">
      <PageHeader
        icon={Key}
        eyebrow={user?.role === 'SUPER_ADMIN' ? 'Platform workspace' : 'Access control'}
        title={t('apiKeys.title')}
        subtitle={t('apiKeys.subtitle')}
        actions={(
          <Button onClick={() => setIsCreateDialogOpen(true)}>
          <Plus className="h-4 w-4 mr-2" />
          {t('apiKeys.generateKey')}
          </Button>
        )}
      />

      <Card>
        <CardHeader>
          <div className="flex flex-wrap items-center justify-between gap-2">
            <CardTitle>{t('apiKeys.allApiKeys')}</CardTitle>
            <Badge variant="secondary">{apiKeys.length} configured</Badge>
          </div>
          <CardDescription>
            Programmatic API credentials. Raw secrets are shown once at creation.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Table className="min-w-[780px]">
            <TableHeader>
              <TableRow>
                <TableHead>{t('users.userName')}</TableHead>
                <TableHead>{t('apiKeys.table.keyPrefix')}</TableHead>
                <TableHead>{t('apiKeys.table.permissions')}</TableHead>
                <TableHead>{t('licenses.status')}</TableHead>
                <TableHead>{t('apiKeys.table.lastUsed')}</TableHead>
                <TableHead>{t('apiKeys.table.expires')}</TableHead>
                <TableHead className="text-right">{t('users.actions')}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {apiKeys.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} className="h-24 text-center text-muted-foreground">
                    {t('dashboard.noData')}
                  </TableCell>
                </TableRow>
              ) : (
                apiKeys.map((key) => (
                  <TableRow key={key.id}>
                    <TableCell className="font-medium">{key.name}</TableCell>
                    <TableCell className="font-mono">{key.keyPrefix}...</TableCell>
                    <TableCell>
                      <Badge variant="outline">{key.permissions.length} {t('apiKeys.table.permissions').toLowerCase()}</Badge>
                    </TableCell>
                    <TableCell>
                      <StatusBadge status={key.status} />
                    </TableCell>
                    <TableCell>
                      {key.last_used_at || key.lastUsedAt
                        ? formatDate(key.last_used_at || key.lastUsedAt)
                        : t('licenses.never')}
                    </TableCell>
                    <TableCell>
                      {key.expires_at || key.expiresAt
                        ? formatDate(key.expires_at || key.expiresAt)
                        : t('licenses.never')}
                    </TableCell>
                    <TableCell className="text-right">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleRevoke(key.id)}
                      >
                        {t('apiKeys.revoke')}
                      </Button>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <McpAccess isSuperAdmin={user?.role === 'SUPER_ADMIN'} />

      <CreateApiKeyDialog
        open={isCreateDialogOpen}
        onOpenChange={setIsCreateDialogOpen}
        onSuccess={() => {
          fetchApiKeys();
          // Don't close immediately, let user see the key
        }}
      />
    </div >
  );
}

export default ApiKeys;
